import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger.ts';
import { RecordAny } from '@dealstack/shared/lib/common/common.ts';
import { isNumberString, isUUID } from 'class-validator';
import { isDate } from 'date-fns';
import {
	AccessorFnColumnDef,
	AccessorKeyColumnDef,
	Column,
	ColumnFiltersState,
	ColumnMeta,
	DisplayColumnDef,
	GroupColumnDef,
	Header,
	PaginationState,
	SortingState,
} from '@tanstack/react-table';
import { isValidDate } from 'rxjs/internal/util/isDate';
import {
	UseMantineTableDataOperationsPartialProps,
	useMantineTableDataOperationsFromUrlParamState,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import { ColumnFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';

import {
	MantineTableDataOperations,
	useMantineTableDataOperations,
} from './useMantineTableDataOperations';
import { ManageCapTransactionsTableColumn } from '@dealstack/frontend/shared/cap';
import { useMemo } from 'react';
import { PaginationDto } from '@dealstack/shared/lib/generated';

export interface TableSorting {
	key: string;
	desc?: boolean;
}

export interface TableOperations<F extends RecordAny = RecordAny> {
	filter?: F;
	search?: string;
	sortBy?: TableSorting[];
	pagination?: PaginationDto;
	quickFilters?: Record<string, boolean | string>;
}

export type ExtendedColumnMeta<TData, TValue = unknown> = ColumnMeta<TData, TValue> & {
	isGrow?: boolean;
	widthPercentage?: number;
	disableToggleVisibility?: boolean;
};

export type AppliedFilterOptionDefinition = {
	label: string;
	value: string;
};

export type AppliedFilterFieldDefinition = {
	key: string;
	type: 'text' | 'multi-select';
	options?: AppliedFilterOptionDefinition[];
};

export type MantineFilterMultiSelectProps = {
	data: { label: string; value: string }[];
	// Opt-in server-driven mode. When `serverSide` is true the dropdown skips its
	// client-side slice/filter and instead lazy-loads + searches + paginates via
	// these callbacks. Omitting `serverSide` keeps the default client-side behaviour.
	serverSide?: boolean;
	onDropdownOpen?: () => void;
	onSearchChange?: (searchQuery: string) => void;
	onReachEnd?: () => void;
	isLoading?: boolean;
	hasMore?: boolean;
	showSearch?: boolean;
};

// The async-only subset a data source supplies alongside the loaded `data`.
export type AsyncFilterMultiSelectProps = Omit<MantineFilterMultiSelectProps, 'data'>;

type ExtendColumnDef<TData, TValue, T> = Omit<T, 'meta'> & {
	meta?: ExtendedColumnMeta<TData, TValue>;
	enableGlobalFilter?: boolean;
	filterValueType?: 'uuid' | 'date-range' | 'date' | 'boolean' | 'number' | string;
	filterVariant?: 'date-range' | string;
	filter?: (props: { column: ExtendedColumn<TData, TValue> }) => React.ReactNode;
	filterText?: string;
	showFilter?: boolean;
	footer?: (() => React.ReactNode) | React.ReactNode;
	columnLabel?: string;
	mantineFilterMultiSelectProps?: MantineFilterMultiSelectProps;
	mantineFilterSelectProps?: MantineFilterMultiSelectProps;
	appliedFilterFields?: AppliedFilterFieldDefinition[];
	enableSorting?: boolean;
};

export type ExtendedColumnDef<TData, TValue = unknown> =
	| ExtendColumnDef<TData, TValue, AccessorKeyColumnDef<TData, TValue>>
	| ExtendColumnDef<TData, TValue, AccessorFnColumnDef<TData, TValue>>
	| ExtendColumnDef<TData, TValue, DisplayColumnDef<TData, TValue>>
	| ExtendColumnDef<TData, TValue, GroupColumnDef<TData, TValue>>;

export type ExtendedColumn<TData, TValue = unknown> = Column<TData, TValue> & {
	columnDef: ExtendedColumnDef<TData, TValue>;
};

export type ExtendedHeader<TData, TValue = unknown> = Header<TData, TValue> & {
	column: ExtendedColumn<TData, TValue>;
};

export const getFilterableColumnIds = <TData>(
	columns: ExtendedColumnDef<TData, unknown>[]
): string[] => {
	return columns.flatMap((column) => {
		const nestedColumns = 'columns' in column ? column.columns : undefined;

		if (nestedColumns?.length) {
			return getFilterableColumnIds(nestedColumns as ExtendedColumnDef<TData, unknown>[]);
		}

		return column.showFilter && typeof column.id === 'string' ? [column.id] : [];
	});
};

const hasAccessorKey = <TData extends RecordAny, TValue = unknown>(
	column: ExtendedColumnDef<TData, TValue>
): column is ExtendedColumnDef<TData, TValue> & { accessorKey: string } => {
	return 'accessorKey' in column && typeof column.accessorKey === 'string';
};

export const useMantineTableWithPaginationFilteringSorting = <
	T extends RecordAny,
	F extends RecordAny,
>(
	columns: ExtendedColumnDef<T>[],
	initialTableOptions: UseMantineTableDataOperationsPartialProps = {},
	useStateFromUrlParams = false
): {
	mantineTableDataOperations: MantineTableDataOperations;
	dataOperations: TableOperations<F>;
} => {
	const rawOpsFromState = useMantineTableDataOperations(initialTableOptions);
	const rawOpsFromUrlParams = useMantineTableDataOperationsFromUrlParamState(initialTableOptions);

	const activeRawOps = useStateFromUrlParams ? rawOpsFromUrlParams : rawOpsFromState;
	const { columnFilters, globalSearchString, sorting, pagination, quickFilters } = activeRawOps;

	// old
	const dataOperations = useMemo((): TableOperations<F> => {
		const mappedFilter = mapMantineTableFilterToFilter<T, F>(
			columns,
			columnFilters,
			globalSearchString
		);
		const mappedPagination = mapMantineTablePagination<F>(pagination);
		const mappedSorting = mapMantineTableSorting<F>(sorting);

		return {
			filter: mappedFilter.filter,
			pagination: mappedPagination,
			sortBy: mappedSorting,
			search: mappedFilter.search,
			quickFilters,
		};
	}, [columnFilters, globalSearchString, sorting, pagination, quickFilters, columns]);

	return {
		mantineTableDataOperations: activeRawOps,
		dataOperations,
	} as const;
};

export const mapMantineTableFilterToFilter = <T extends RecordAny, F extends RecordAny>(
	columns: ExtendedColumnDef<T, unknown>[],
	columnsFilters?: ColumnFiltersState,
	globalSearchString?: string
): {
	filter?: F;
	search?: string;
} => {
	// ignore mantine table default filter which always sets the date-range in the urls as [null, null]
	const isMeaningful = (filter: ColumnFilter) => {
		const column = columns.find((c) => {
			const columnKey = hasAccessorKey(c) ? c.accessorKey : c.id;
			return filter.id === columnKey;
		});
		const isDateRange =
			column?.filterValueType === 'date-range' || column?.filterVariant === 'date-range';

		if (isDateRange) {
			return !(
				Array.isArray(filter.value) && filter.value.every((v) => v == null || v === '')
			);
		}

		// for all other filter types keep whatever the user set
		return true;
	};

	const meaningfulFilters = (columnsFilters ?? []).filter(isMeaningful);

	const getColumnKey = (column: ExtendedColumnDef<T, unknown>) => {
		return hasAccessorKey(column) ? column.accessorKey : column.id;
	};

	const mappedColumnsFilters: F | undefined = meaningfulFilters?.length
		? ({
				...meaningfulFilters.reduce(
					(prev, { value, id }) => {
						const key = id;

						if (value === null) {
							if (id === ManageCapTransactionsTableColumn.noType) {
								return {
									...prev,
									types: [null],
								};
							}
							if (id === ManageCapTransactionsTableColumn.sourcePersonId) {
								return {
									...prev,
									sourceIds: [null],
								};
							}
							if (id === ManageCapTransactionsTableColumn.destinationPersonId) {
								return {
									...prev,
									destinationIds: [null],
								};
							}
							return {
								...prev,
								[key]: null,
							};
						}

						if (value === false) {
							return {
								...prev,
								[key]: false,
							};
						}

						if (value === undefined || value === '') {
							return prev;
						}

						const column = columns.find((column) => id === getColumnKey(column));
						if (!column) {
							return prev;
						}

						return {
							...prev,
							...makeFilterPair(column.filterValueType, key, value),
						};
					},
					{} as Record<string, unknown>
				),
			} as F)
		: undefined;

	if (!mappedColumnsFilters && !globalSearchString) {
		return {};
	}

	return { filter: mappedColumnsFilters, search: globalSearchString };
};

export const makeFilterPair = (filterValueType, key, value) => {
	switch (filterValueType) {
		case 'uuid': {
			if (Array.isArray(value)) {
				const ids = value.filter((id): id is string => isUUID(id));

				return ids.length ? { [key]: ids } : undefined;
			}

			if (!isUUID(value)) {
				return;
			}

			return {
				[key]: value as string,
			};
		}
		case 'date-range': {
			const [from, to] = Array.isArray(value) ? value : [];
			const isFromIsValidDate = isValidDate(from);
			const isToIsValidDate = isValidDate(to);

			if (!isFromIsValidDate && !isToIsValidDate) {
				logger.error(`Invalid date range value: ${value}`);
				return;
			}

			if (isFromIsValidDate && !isToIsValidDate) {
				return {
					[key]: {
						from,
					},
				};
			}
			if (!isFromIsValidDate && isToIsValidDate) {
				return {
					[key]: {
						to,
					},
				};
			}
			return {
				[key]: {
					from,
					to,
				},
			};
		}
		case 'date': {
			if (!isDate(value)) {
				logger.error(`Invalid date value: ${value}`);
				return;
			}

			return {
				[key]: value as Date,
			};
		}
		case 'boolean': {
			let val = false;

			if (typeof value === 'boolean') {
				val = value;
			} else if (typeof value === 'string') {
				val = value === 'true';
			} else {
				val = !!value;
			}

			return {
				[key]: val,
			};
		}
		case 'number': {
			if (!isNumberString(value)) {
				logger.error(`Invalid number string value: ${value}`);
				return;
			}

			return {
				[key]: Number(value),
			};
		}
		default:
			return {
				[key]: value,
			};
	}
};

export const mapMantineTableSorting = <F extends RecordAny>(
	sorting?: SortingState
): TableOperations<F>['sortBy'] => {
	return sorting?.map((sort) => ({
		key: sort.id,
		desc: sort.desc,
	}));
};

export const mapMantineTablePagination = <F extends RecordAny>({
	pageIndex,
	pageSize,
}: PaginationState): TableOperations<F>['pagination'] => {
	return {
		take: pageSize,
		skip: pageIndex * pageSize,
	};
};
